<template>
  <div class="flex flex-col gap-4 w-full h-full overflow-hidden">
    <!-- Header Section -->
    <div ref="headerRef" class="flex flex-row justify-between items-center flex-shrink-0">
      <div class="flex items-center gap-2">
        <h1 class="text-2xl font-bold">{{ title }}</h1>
        <i v-if="showInfo" class="pi pi-info-circle text-gray-400 cursor-help" :title="infoText"></i>
      </div>
      <div class="flex gap-2">
        <slot name="header-actions"></slot>
        <Button 
          v-if="shouldShowNewButton"
          :label="newButtonLabel" 
          icon="pi pi-plus"
          severity="primary"
          @click="$emit('new')"
        />
      </div>
    </div>
    
    <!-- Filters and Search Bar -->
    <div ref="filtersRef" class="flex flex-col gap-3 p-4 bg-white rounded-lg border border-gray-200 flex-shrink-0">
      <div class="flex flex-wrap gap-3 items-center justify-between">
        <!-- Left side: Custom Select Filters (toolbar) or filter menu button -->
        <div class="flex flex-wrap gap-3 items-center">
          <div v-for="filter in toolbarFilters" :key="filter.key" class="flex items-center gap-2">
            <InputText
              v-if="filter.type === 'text'"
              v-model="filterValues[filter.key]"
              :placeholder="filter.placeholder || filter.label || ''"
              class="w-[200px]"
              @update:modelValue="onFilterChange"
            />
            <MultiSelect
              v-else-if="filter.multiple"
              v-model="filterValues[filter.key]"
              :options="filter.options"
              :optionLabel="filter.optionLabel || 'label'"
              :optionValue="filter.optionValue || 'value'"
              :placeholder="filter.placeholder || 'Selecciona...'"
              :showClear="filter.showClear !== false"
              :filter="filter.filter !== false"
              :filterPlaceholder="filter.filterPlaceholder || 'Buscar...'"
              class="w-[200px]"
              @update:modelValue="onFilterChange"
            />
            <Select
              v-else
              v-model="filterValues[filter.key]"
              :options="filter.options"
              :optionLabel="filter.optionLabel || 'label'"
              :optionValue="filter.optionValue || 'value'"
              :placeholder="filter.placeholder || 'Selecciona...'"
              :showClear="filter.showClear !== false"
              :filter="filter.filter !== false"
              :filterPlaceholder="filter.filterPlaceholder || 'Buscar...'"
              class="w-[200px]"
              @update:modelValue="onFilterChange"
            />
          </div>

          <!-- Filter menu button -->
          <Button
            v-if="shouldShowFiltersMenu"
            :label="filtersMenuButtonLabel"
            icon="pi pi-filter"
            outlined
            severity="primary"
            @click="showFilterDialog = true"
          />

          <!-- Export/Download Button -->
          <Button
            v-if="showExportButton"
            icon="pi pi-download"
            outlined
            rounded
            @click="$emit('export')"
          />
        </div>

        <!-- Right side: Search and Date Range -->
        <div class="flex flex-wrap gap-3 items-center">
          <!-- Search Input -->
          <div v-if="showSearch" class="flex items-center gap-2">
              <InputText
                v-model="searchText"
                :placeholder="searchPlaceholderComputed"
                class="w-[280px]"
                @input="onSearchChange"
              />
          </div>
          <!-- Date Range Picker (Optional) -->
          <div v-if="showDateRange" class="flex items-center gap-2">
            <DatePicker
              v-model="dateRange"
              selectionMode="range"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :placeholder="datePlaceholder"
              class="w-[280px]"
              @update:modelValue="onDateRangeChange"
            />
            <Button
              v-if="csvDownloadUrl"
              icon="pi pi-download"
              severity="primary"
              class="bg-primary"
              :loading="downloadingCsv"
              @click="handleCsvDownload"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="bg-white rounded-lg border border-gray-200 flex-1 flex flex-col min-h-0 overflow-hidden">
      <DataTable
        :value="filteredData"
        :paginator="pagination"
        :rows="rowsPerPage"
        :rowsPerPageOptions="rowsPerPageOptions"
        :loading="loading || internalLoading"
        :emptyMessage="emptyMessage"
        :scrollable="isScrollable"
        :scrollHeight="computedScrollHeight"
        :sortField="defaultSortField"
        :sortOrder="defaultSortOrder"
        :pt="dataTablePt"
        tableStyle="min-width: 50rem"
        class="p-datatable-sm h-full"
        @row-click="onRowClick"
      >
        <template #header v-if="$slots.tableHeader">
          <slot name="tableHeader"></slot>
        </template>

        <!-- Dynamic Columns -->
        <Column
          v-for="column in columnsToUse"
          :key="column.field"
          :field="column.field"
          :header="column.header"
          :sortable="column.sortable !== false"
          :sortField="defaultSortField === column.field ? defaultSortField : undefined"
          :sortOrder="defaultSortField === column.field ? defaultSortOrder : undefined"
          :style="column.style"
          :class="column.class"
        >
          <template v-if="column.bodyTemplate" #body="{ data }">
            <slot :name="`body-${column.field}`" :data="data" :row="data">
              <!-- Auto-render based on renderType or field name patterns -->
              <!-- Date columns -->
              <span v-if="shouldFormatDate(column.field) && !column.renderType">
                {{ formatDateValue(data[column.field]) }}
              </span>
              <!-- URL link rendering -->
              <template v-if="column.renderType === 'url' || shouldRenderAsUrl(column.field, data)">
                <a
                  v-if="shouldRenderAsUrl(column.field, data)"
                  :href="getUrlValue(column, data)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary hover:underline"
                  @click.stop
                >
                  {{ getUrlLabel(column, data) }}
                </a>
                <span v-else>
                  {{ data[column.field] }}
                </span>
              </template>
              <!-- Truncated text with tooltip -->
              <span
                v-else-if="column.renderType === 'truncate' || shouldTruncate(column.field)"
                v-tooltip="getTooltipValue(data[column.field])"
              >
                {{ truncateText(data[column.field], 11) }}
              </span>
              <!-- User field -->
              <Tag
                v-else-if="column.renderType === 'user' || column.field === 'user'"
                :value="getUserName(data[column.field])"
                severity="info"
              />
              <!-- Tag rendering -->
              <Tag
                v-else-if="column.renderType === 'tag' && column.tagConfig"
                :value="getTagLabel(column, data[column.field])"
                :severity="getTagSeverity(column, data[column.field])"
              />
            </slot>
          </template>
          <template v-else-if="column.formatter" #body="{ data }">
            {{ column.formatter ? column.formatter(data[column.field], data) : '' }}
          </template>
        </Column>

        <!-- Actions Column (if provided) -->
        <Column v-if="shouldShowActions || actionButtons" :header="t('general.actions')" :style="{ width: actionButtons ? '180px' : '120px' }">
          <template #body="{ data: row }">
            <slot name="actions" :row="row">
              <!-- Built-in action buttons -->
              <div v-if="actionButtons" class="flex gap-1">
                <Button
                  v-if="actionButtons.edit"
                  icon="pi pi-pencil"
                  text
                  rounded
                  size="small"
                  v-tooltip="'Editar'"
                  @click.stop="handleEdit(row)"
                />
                <Button
                  v-if="actionButtons.download"
                  icon="pi pi-download"
                  text
                  rounded
                  size="small"
                  v-tooltip="'Descargar'"
                  @click.stop="handleDownload(row)"
                />
                <Button
                  v-if="actionButtons.duplicate"
                  icon="pi pi-copy"
                  text
                  rounded
                  size="small"
                  :loading="duplicatingId === row.id"
                  v-tooltip="'Duplicar'"
                  @click.stop="handleDuplicate(row)"
                />
                <Button
                  v-if="actionButtons.delete"
                  icon="pi pi-trash"
                  text
                  rounded
                  severity="danger"
                  size="small"
                  v-tooltip="'Eliminar'"
                  @click.stop="handleDelete(row)"
                />
              </div>
            </slot>
          </template>
        </Column>

        <template #empty>
          <div class="text-center py-8 text-gray-500">
            {{ emptyMessage }}
          </div>
        </template>

        <template #loading>
          <div class="text-center py-8">
            <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Filter Dialog -->
    <Dialog
      :visible="showFilterDialog"
      :header="filterDialogTitleComputed"
      :modal="true"
      :style="{ width: 'min(520px, 95vw)' }"
      @update:visible="showFilterDialog = $event"
    >
      <slot v-if="$slots['filter-dialog']" name="filter-dialog" />
      <div v-else-if="filtersInDialog && internalDialogFilters.length" class="flex flex-col gap-5">
        <div
          v-for="filter in internalDialogFilters"
          :key="filter.key"
          class="flex flex-col gap-1"
        >
          <label :for="`filter-${filter.key}`" class="font-medium text-gray-800">
            {{ filter.label }}
          </label>
          <p v-if="filter.description" class="text-sm text-gray-500 m-0">
            {{ filter.description }}
          </p>
          <MultiSelect
            v-if="filter.multiple"
            :id="`filter-${filter.key}`"
            v-model="filterValues[filter.key]"
            :options="filter.options"
            :optionLabel="filter.optionLabel || 'label'"
            :optionValue="filter.optionValue || 'value'"
            :placeholder="filter.placeholder"
            :showClear="filter.showClear !== false"
            :filter="filter.filter !== false"
            :filterPlaceholder="filter.filterPlaceholder || t('general.search')"
            class="w-full"
          />
          <InputText
            v-else-if="filter.type === 'text'"
            :id="`filter-${filter.key}`"
            v-model="filterValues[filter.key]"
            :placeholder="filter.placeholder || filter.label || ''"
            class="w-full"
          />
          <Select
            v-else
            :id="`filter-${filter.key}`"
            v-model="filterValues[filter.key]"
            :options="filter.options"
            :optionLabel="filter.optionLabel || 'label'"
            :optionValue="filter.optionValue || 'value'"
            :placeholder="filter.placeholder"
            :showClear="filter.showClear !== false"
            :filter="filter.filter !== false"
            :filterPlaceholder="filter.filterPlaceholder || t('general.search')"
            class="w-full"
          />
        </div>
      </div>
      <template #footer>
        <Button
          v-if="filtersInDialog && internalDialogFilters.length"
          :label="t('general.clear_filters')"
          icon="pi pi-filter-slash"
          text
          @click="clearDialogFilters"
        />
        <Button :label="t('general.cancel')" icon="pi pi-times" text @click="showFilterDialog = false" />
        <Button
          :label="t('general.apply_filters')"
          icon="pi pi-check"
          severity="primary"
          @click="applyAdvancedFilters"
        />
      </template>
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <Dialog
      :visible="showDeleteDialog"
      :header="deleteDialogTitle"
      :modal="true"
      :style="{ width: '450px' }"
      @update:visible="showDeleteDialog = $event"
    >
      <p>{{ deleteDialogMessage }}</p>
      <template #footer>
        <Button
          :label="deleteDialogCancelLabel"
          icon="pi pi-times"
          text
          @click="showDeleteDialog = false"
        />
        <Button
          :label="deleteDialogConfirmLabel"
          icon="pi pi-check"
          severity="danger"
          :loading="deleting"
          @click="confirmDelete"
        />
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import DatePicker from 'primevue/datepicker'
import Select from 'primevue/select'
import MultiSelect from 'primevue/multiselect'
import Dialog from 'primevue/dialog'
import Tag from 'primevue/tag'
import { useToast } from 'primevue/usetoast'
import dayjs from '@/utils/dayjs'
import { useAuthStore } from '@/stores/auth'
import { useStoreI18n } from '@/composables/useStoreI18n'
import { axiosToken } from '@/utils/http'

// Types
export interface ColumnDefinition {
  field: string
  header: string
  sortable?: boolean
  style?: string
  class?: string
  formatter?: (value: any, row: any) => string
  bodyTemplate?: boolean
  renderType?: 'url' | 'truncate' | 'user' | 'tag' | 'default' // Auto-render types
  urlField?: string // Field name for URL (default: field + '_url')
  maxWidth?: string // For truncate type
  tagConfig?: { // For tag type
    options: Array<{ value: any; label: string; severity: 'info' | 'success' | 'warning' | 'danger' }>
    defaultSeverity?: 'info' | 'success' | 'warning' | 'danger'
  }
}

export interface CustomFilter {
  key: string
  label?: string
  description?: string
  type?: 'select' | 'select-multiple' | 'text' | 'search'
  options?: Array<{ label: string; value: any }>
  optionLabel?: string
  optionValue?: string
  placeholder?: string
  showClear?: boolean
  multiple?: boolean
  filter?: boolean
  filterPlaceholder?: string
}

export interface ActionButtons {
  edit?: boolean | string // true or URL pattern like '/budget/budgetdetail/{id}'
  delete?: boolean
  duplicate?: boolean | string // true or URL pattern
  download?: boolean
}

export interface ListConfig {
  filterFunction?: (data: any[], filters: any) => any[] // Custom filter function
  editUrl?: string | ((row: any) => string) // URL pattern or function for edit
  rowClickUrl?: string | ((row: any) => string) // URL pattern or function for row click
  deleteFunction?: (id: any) => Promise<any> // Delete function
  duplicateFunction?: (id: any) => Promise<any> // Duplicate function
  downloadFunction?: (id: any) => Promise<any> // Download function
  refreshFunction?: () => Promise<void> // Refresh data function
}

export interface DataFilter {
  fetch_content?: (params?: { dateRange?: [Date | null, Date | null] | null; filters?: any }) => Promise<void> // Fetch data function
  create_table_cols?: () => any[] // Create columns function (can return naive-ui or PrimeVue format)
  create_filter_object?: () => Promise<any[]> // Create filter definitions
  filter_gateway?: (filters: any) => any[] // Filter data function
  deleteEvent?: (id: any) => Promise<any> // Delete function
  downloadEvent?: (id: any) => Promise<any> // Download function
  url_edit?: string // Edit URL pattern
  url_duplicate?: string // Duplicate URL pattern
  load_row_props?: (row: any) => any // Row click handler
  rowClickUrl?: string | ((row: any) => string) // URL pattern or function for row click navigation
  columnConfig?: Record<string, Partial<ColumnDefinition>> // Column-specific configuration overrides
}

// Props
const props = withDefaults(defineProps<{
  title?: string
  data: any[]
  columns?: ColumnDefinition[] // Optional when dataFilter is provided
  searchColumns?: string[] // Columns to search in
  dateColumn?: string // Column name to use for date filtering (auto-detected if not provided)
  defaultDateRange?: [Date | null, Date | null] | null // Default date range (if not provided, uses current year to next year)
  customFilters?: CustomFilter[]
  showDateRange?: boolean
  showSearch?: boolean
  showFilterButton?: boolean
  filtersInDialog?: boolean
  showExportButton?: boolean
  csvDownloadUrl?: string
  csvDownloadFilename?: string
  showNewButton?: boolean
  showActions?: boolean
  showInfo?: boolean
  infoText?: string
  newButtonLabel?: string
  searchPlaceholder?: string
  datePlaceholder?: string
  filterDialogTitle?: string
  emptyMessage?: string
  loading?: boolean
  pagination?: boolean
  rowsPerPage?: number
  rowsPerPageOptions?: number[]
  scrollable?: boolean
  scrollHeight?: string
  actionButtons?: ActionButtons
  listConfig?: ListConfig
  dataFilter?: DataFilter // Alternative to listConfig - accepts dataFilter object like
  deleteDialogTitle?: string
  deleteDialogMessage?: string
  deleteDialogCancelLabel?: string
  deleteDialogConfirmLabel?: string
}>(), {
  title: 'Lista',
  data: () => [],
  columns: undefined,
  searchColumns: () => [],
  customFilters: () => [],
  showDateRange: true,
  showSearch: true, // Default true - most lists need search
  showFilterButton: false,
  filtersInDialog: false,
  showExportButton: false,
  csvDownloadUrl: undefined,
  csvDownloadFilename: 'export.csv',
  showNewButton: undefined, // Auto-detect: true if @new handler exists or dataFilter has url_edit
  showActions: undefined, // Auto-detect: true if actionButtons provided
  showInfo: false,
  infoText: '',
  newButtonLabel: 'Nuevo',
  searchPlaceholder: 'Buscar...',
  datePlaceholder: 'Rango de fechas',
  filterDialogTitle: 'Filtros',
  emptyMessage: 'No se encontraron registros',
  loading: false,
  pagination: true,
  rowsPerPage: 50, // Default 50 - most lists use 50
  rowsPerPageOptions: () => [10, 25, 50, 100],
  scrollable: true, // Enable scrollable by default for internal table scrolling
  scrollHeight: undefined, // Auto-calculate based on viewport height
  actionButtons: undefined,
  listConfig: undefined,
  dataFilter: undefined,
  deleteDialogTitle: 'Eliminar',
  deleteDialogMessage: '¿Estás seguro de eliminar este registro?',
  deleteDialogCancelLabel: 'Cancelar',
  deleteDialogConfirmLabel: 'Eliminar'
})

// Emits
const emit = defineEmits<{
  (e: 'new'): void
  (e: 'export'): void
  (e: 'row-click', row: any): void
  (e: 'filter-change', filters: any): void
  (e: 'date-range-change', range: [Date | null, Date | null] | null): void
  (e: 'edit', row: any): void
  (e: 'delete', row: any): void
  (e: 'duplicate', row: any): void
  (e: 'download', row: any): void
  (e: 'refresh'): void
}>()

// State
const searchText = ref('')
const dateRange = ref<[Date | null, Date | null] | null>(null)
const filterValues = ref<Record<string, any>>({})
const showFilterDialog = ref(false)
const showDeleteDialog = ref(false)
const deleting = ref(false)
const duplicatingId = ref<any>(null)
const selectedRow = ref<any>(null)
const toast = useToast()
const { t } = useStoreI18n()
const internalLoading = ref(false)
const downloadingCsv = ref(false)
const internalColumns = ref<ColumnDefinition[]>([])
const internalCustomFilters = ref<CustomFilter[]>([])
const internalDialogFilters = ref<CustomFilter[]>([])
const filterObject = ref<any[]>([])
const filterDefaults = ref<Record<string, any>>({})
const authStore = useAuthStore()
const viewportHeight = ref(window.innerHeight)
const defaultSortField = ref<string | undefined>(undefined)
const defaultSortOrder = ref<1 | -1 | 0 | undefined>(undefined)
const headerRef = ref<HTMLElement | null>(null)
const filtersRef = ref<HTMLElement | null>(null)
const headerHeight = ref(0)
const filtersHeight = ref(0)
let resizeObserver: any = null

// Update viewport height on resize
const updateViewportHeight = () => {
  viewportHeight.value = window.innerHeight
  updateElementHeights()
}

// Update element heights
const updateElementHeights = () => {
  nextTick(() => {
    if (headerRef.value) {
      headerHeight.value = headerRef.value.offsetHeight
    }
    if (filtersRef.value) {
      filtersHeight.value = filtersRef.value.offsetHeight
    }
  })
}

// Computed: Determine if table should be scrollable
const isScrollable = computed(() => {
  // If scrollable prop is explicitly set, use it
  if (props.scrollable !== undefined) {
    return props.scrollable
  }
  // Otherwise, enable scrollable for better height adaptation
  return true
})

// Computed: Calculate scroll height based on viewport
const computedScrollHeight = computed(() => {
  // If scrollable is explicitly false, don't calculate scroll height
  if (props.scrollable === false) {
    return props.scrollHeight || undefined
  }
  
  // If scrollHeight is explicitly provided, use it
  if (props.scrollHeight) {
    return props.scrollHeight
  }
  
  // Calculate available height: viewport height minus actual header, filters, padding, and pagination
  const gapsPadding = 32 // gap-4 = 16px * 2
  const paginationHeight = props.pagination ? 60 : 0
  const tableContainerPadding = 16 // Border and padding of table container
  const bottomMargin = 48 // Additional margin at the bottom to prevent scrollbar
  
  const calculatedHeight = viewportHeight.value - headerHeight.value - filtersHeight.value - gapsPadding - paginationHeight - tableContainerPadding - bottomMargin
  
  // Ensure minimum height of 300px
  return `${Math.max(300, calculatedHeight)}px`
})

// Computed: Auto-detect showNewButton - show if actionButtons provided or dataFilter has url_edit
const shouldShowNewButton = computed(() => {
  // If explicitly set to true, show it
  if (props.showNewButton === true) return true
  // If explicitly set to false, hide it
  if (props.showNewButton === false) return false
  // Auto-detect: show if dataFilter has url_edit (indicates CRUD operations)
  return !!props.dataFilter?.url_edit
})

// Computed: Auto-detect showActions - show if actionButtons provided
const shouldShowActions = computed(() => {
  // If explicitly set to true, show it
  if (props.showActions === true) return true
  // If explicitly set to false, hide it
  if (props.showActions === false) return false
  // Auto-detect: show if actionButtons provided
  return !!props.actionButtons
})

// Initialize date range to one year ago to today
onMounted(async () => {
  // Set up resize listener for viewport height
  window.addEventListener('resize', updateViewportHeight)
  
  // Set up ResizeObserver to watch for header and filters height changes
  await nextTick()
  
  if (typeof (window as any).ResizeObserver !== 'undefined') {
    resizeObserver = new (window as any).ResizeObserver(() => {
      updateElementHeights()
    })
    
    if (headerRef.value) {
      resizeObserver.observe(headerRef.value)
    }
    if (filtersRef.value) {
      resizeObserver.observe(filtersRef.value)
    }
  } else {
    // Fallback: update heights after a short delay
    setTimeout(updateElementHeights, 100)
  }
  
  // Initial height measurement
  updateElementHeights()
  
  // Initialize date range first (needed for fetch_content)
  if (props.showDateRange) {
    if (props.defaultDateRange) {
      // Use provided default date range
      dateRange.value = props.defaultDateRange
    } else {
      // Default: current year to next year
      const today = new Date()
      const currentYearStart = new Date(today.getFullYear(), 0, 1) // January 1st of current year
      const nextYearEnd = new Date(today.getFullYear() + 1, 11, 31) // December 31st of next year
      dateRange.value = [currentYearStart, nextYearEnd]
    }
    emit('date-range-change', dateRange.value)
  }
  
  // If dataFilter is provided, use it to initialize
  if (props.dataFilter) {
    internalLoading.value = true
    
    // Create columns from dataFilter
    if (props.dataFilter.create_table_cols) {
      const naiveColumns = props.dataFilter.create_table_cols()
      // Convert naive-ui columns to PrimeVue columns
      internalColumns.value = naiveColumns.map((col: any) => {
        const field = col.key
        const isDateField = isDateColumn(field)
        const isUserField = field === 'user' || field.toLowerCase().includes('user')
        const isTruncateField = shouldTruncate(field)
        const isUrlField = field.includes('_name') || field.includes('place') || field.includes('location')
        
        // Check for default sort order
        if (col.defaultSortOrder) {
          defaultSortField.value = field
          // Convert "ascend" to 1, "descend" to -1
          defaultSortOrder.value = col.defaultSortOrder === 'ascend' ? 1 : col.defaultSortOrder === 'descend' ? -1 : undefined
        }
        
        // Determine render type
        let renderType: 'url' | 'truncate' | 'user' | 'tag' | 'default' | undefined = undefined
        if (isUserField) {
          renderType = 'user'
        } else if (isTruncateField && !col.render) {
          renderType = 'truncate'
        } else if (isUrlField && !col.render) {
          renderType = 'url'
        }
        
        // Check for state field and configure tag rendering
        let tagConfig: ColumnDefinition['tagConfig'] = undefined
        if (field === 'state' || field.toLowerCase().includes('status')) {
          // Try to detect state values from the data or use default config
          tagConfig = {
            options: [
              { value: 0, label: 'Open', severity: 'info' },
              { value: 1, label: 'Won', severity: 'success' },
              { value: 2, label: 'Lost', severity: 'warning' },
              { value: null, label: 'Open', severity: 'info' }
            ],
            defaultSeverity: 'info'
          }
          renderType = 'tag'
        }
        
        // Apply column config overrides if provided
        const columnOverride = props.dataFilter?.columnConfig?.[field]

        const renderUsesBodyTemplate = !!renderType || isDateField || (
          !!col.render && ['booking_items', 'place_name', 'extra_info_internal'].includes(field)
        )

        const formatter = col.render
          ? (_value: any, row: any) => {
              const result = col.render(row)
              if (result === null || result === undefined) return ''
              if (typeof result === 'string' || typeof result === 'number' || typeof result === 'boolean') {
                return String(result)
              }
              return ''
            }
          : (value: any) => String(value ?? '')

        const bodyTemplate = columnOverride?.bodyTemplate !== undefined
          ? columnOverride.bodyTemplate
          : renderUsesBodyTemplate
        
        return {
          field: field,
          header: col.title,
          sortable: !!col.sorter,
          bodyTemplate,
          renderType: columnOverride?.renderType || renderType,
          maxWidth: columnOverride?.maxWidth || (isTruncateField ? '300px' : undefined),
          tagConfig: columnOverride?.tagConfig || tagConfig,
          urlField: columnOverride?.urlField,
          formatter,
        }
      })
    }
    
    // Create filters from dataFilter
    if (props.dataFilter.create_filter_object) {
      filterObject.value = await props.dataFilter.create_filter_object()
      // Convert to CustomFilter format
      const mapFilterDef = (f: any): CustomFilter => ({
        key: f.id,
        label: f.label,
        description: f.description,
        type: f.type,
        options: f.data || [],
        placeholder: f.placeholder || f.label,
        showClear: f.showClear !== false,
        multiple: f.type === 'select-multiple',
        filter: f.filter !== false,
        filterPlaceholder: f.filterPlaceholder,
      })

      const toolbarCompatibleFilters = filterObject.value.filter(
        (f: any) => f.type === 'select' || f.type === 'select-multiple' || f.type === 'text'
      )

      internalDialogFilters.value = toolbarCompatibleFilters.map(mapFilterDef)
      internalCustomFilters.value = props.filtersInDialog ? [] : internalDialogFilters.value

      const getDefaultValue = (f: any) => {
        if (f.default !== undefined) {
          if (f.type === 'select-multiple') {
            return Array.isArray(f.default) ? [...f.default] : [f.default]
          }
          return f.default
        }
        if (f.type === 'select-multiple') return []
        if (f.type === 'text') return ''
        return null
      }

      // Initialize filter values and defaults
      filterObject.value.forEach((f: any) => {
        const defaultValue = getDefaultValue(f)
        filterDefaults.value[f.id] = Array.isArray(defaultValue) ? [...defaultValue] : defaultValue
        filterValues.value[f.id] = Array.isArray(defaultValue) ? [...defaultValue] : defaultValue
      })
    }
    
    // Fetch content after date range and filters are initialized
    // Only pass date range if both dates are set and showDateRange is enabled
    if (props.dataFilter.fetch_content) {
      const fetchParams: { dateRange?: [Date | null, Date | null] | null; filters?: any } = {}
      
      if (props.showDateRange && dateRange.value && dateRange.value[0] && dateRange.value[1]) {
        fetchParams.dateRange = dateRange.value
      }
      
      if (Object.keys(filterValues.value).length > 0) {
        fetchParams.filters = filterValues.value
      }
      
      await props.dataFilter.fetch_content(Object.keys(fetchParams).length > 0 ? fetchParams : undefined)
    }
    
    internalLoading.value = false
  } else {
    // Use provided columns
    internalColumns.value = props.columns || []
  }

  // Initialize filter values for custom filters
  props.customFilters.forEach(filter => {
    filterValues.value[filter.key] = null
  })
})

// Use internal columns if dataFilter is provided, otherwise use props.columns
const columnsToUse = computed(() => {
  return props.dataFilter ? internalColumns.value : (props.columns || [])
})

// Use internal filters if dataFilter is provided, otherwise use props.customFilters
const filtersToUse = computed(() => {
  return props.dataFilter ? internalCustomFilters.value : props.customFilters
})

const toolbarFilters = computed(() => {
  if (props.filtersInDialog) return []
  return filtersToUse.value
})

const shouldShowFiltersMenu = computed(() => {
  if (props.showFilterButton) return true
  return props.filtersInDialog && internalDialogFilters.value.length > 0
})

const filterDialogTitleComputed = computed(() => {
  if (props.filterDialogTitle && props.filterDialogTitle !== 'Filtros') {
    return props.filterDialogTitle
  }
  if (props.filtersInDialog) {
    return t('bookings.filter_dialog_title')
  }
  return t('general.filters')
})

const filtersMenuButtonLabel = computed(() => {
  const base = t('general.filters')
  const count = activeFiltersCount.value
  return count > 0 ? `${base} (${count})` : base
})

const isFilterValueActive = (filterDef: any, value: any, defaultValue: any): boolean => {
  if (filterDef.type === 'select-multiple') {
    const current = Array.isArray(value) ? value : []
    const defaults = Array.isArray(defaultValue) ? defaultValue : []
    if (current.length !== defaults.length) return true
    return !current.every((item) => defaults.includes(item))
  }
  return value !== defaultValue && value !== null && value !== ''
}

const activeFiltersCount = computed(() => {
  if (!props.filtersInDialog) return 0
  return filterObject.value.reduce((count, filterDef: any) => {
    if (filterDef.type === 'search') return count
    if (filterDef.type !== 'select' && filterDef.type !== 'select-multiple' && filterDef.type !== 'text') return count
    return isFilterValueActive(
      filterDef,
      filterValues.value[filterDef.id],
      filterDefaults.value[filterDef.id]
    )
      ? count + 1
      : count
  }, 0)
})

const clearDialogFilters = () => {
  Object.keys(filterDefaults.value).forEach((key) => {
    const defaultValue = filterDefaults.value[key]
    filterValues.value[key] = Array.isArray(defaultValue) ? [...defaultValue] : defaultValue
  })
}

// Computed: Filtered data
const filteredData = computed(() => {
  let result = [...props.data]

  // If dataFilter is provided, use its filter_gateway
  if (props.dataFilter?.filter_gateway) {
    // Build filter object from filterValues (but exclude search to handle it separately)
    const filterObj: any = { ...filterValues.value }
    
    // Don't pass search to filter_gateway if we want to search all columns
    // Remove search from filterObj so filter_gateway doesn't limit results
    const searchFilter = filterObject.value.find((f: any) => f.type === 'search')
    if (searchFilter && filterObj[searchFilter.id]) {
      delete filterObj[searchFilter.id]
    }
    if (filterObj.search) {
      delete filterObj.search
    }
    
    // Convert select-multiple values to arrays if they're not already
    filterObject.value.forEach((f: any) => {
      if (f.type === 'select-multiple' && filterObj[f.id] !== null && filterObj[f.id] !== undefined) {
        // Ensure it's an array
        if (!Array.isArray(filterObj[f.id])) {
          filterObj[f.id] = [filterObj[f.id]]
        }
      }
    })
    
    // Remove empty arrays from filterObj (they shouldn't trigger filtering)
    Object.keys(filterObj).forEach(key => {
      if (Array.isArray(filterObj[key]) && filterObj[key].length === 0) {
        delete filterObj[key]
      }
    })
    
    // DEBUG: Log filter execution
    console.log('🔍 [List.vue] Filter execution:', {
      filterValues: filterValues.value,
      filterObj: filterObj,
      hasActivityFilter: !!filterObj.activity,
      activityValue: filterObj.activity,
      activityIsArray: Array.isArray(filterObj.activity)
    })
    
    // Apply filter_gateway without search (for other filters like state)
    result = props.dataFilter.filter_gateway(filterObj)
    
    // DEBUG: Log filter result
    console.log('✅ [List.vue] Filter result:', {
      originalCount: props.data.length,
      filteredCount: result.length,
      sampleResult: result.slice(0, 3)
    })
    
    // Apply text search across all columns if search text exists
    if (searchText.value && columnsToUse.value.length > 0) {
      const searchLower = searchText.value.toLowerCase()
      result = result.filter((row: any) => {
        return columnsToUse.value.some(column => {
          const value = getColumnValue(row, column.field)
          if (value === null || value === undefined || value === '') return false
          
          // For date columns, also search in formatted date
          if (isDateColumn(column.field)) {
            const formattedDate = formatDateValue(value)
            return String(value).toLowerCase().includes(searchLower) || 
                   formattedDate.toLowerCase().includes(searchLower)
          }
          
          return String(value).toLowerCase().includes(searchLower)
        })
      })
    }
    
    // Apply date filtering if needed
    if (props.showDateRange && dateRange.value && dateRange.value[0] && dateRange.value[1]) {
      let dateColumnField = props.dateColumn
      if (!dateColumnField) {
        const dateColumn = columnsToUse.value.find(col => 
          col.field.toLowerCase().includes('date') || 
          col.field.toLowerCase().includes('fecha') ||
          col.field.toLowerCase().includes('created') ||
          col.field.toLowerCase().includes('updated')
        )
        dateColumnField = dateColumn?.field
      }
      if (dateColumnField) {
        result = result.filter((row: any) => {
          const rowDate = row[dateColumnField!]
          // Include rows with null/undefined dates when date range filter is active
          if (!rowDate) return true
          const date = dayjs(rowDate).toDate()
          return date >= dateRange.value![0]! && date <= dateRange.value![1]!
        })
      }
    }
    return result
  }

  // Apply custom filter function if provided
  if (props.listConfig?.filterFunction) {
    result = props.listConfig.filterFunction(result, {
      search: searchText.value,
      filters: filterValues.value,
      dateRange: dateRange.value
    })
    // Return early if custom filter function is used
    return result
  }

  // Apply date range filter
  if (props.showDateRange && dateRange.value && dateRange.value[0] && dateRange.value[1]) {
    // Use specified date column or auto-detect
    let dateColumnField = props.dateColumn
    
    if (!dateColumnField) {
      const cols = columnsToUse.value
      const dateColumn = cols.find(col => 
        col.field.toLowerCase().includes('date') || 
        col.field.toLowerCase().includes('fecha') ||
        col.field.toLowerCase().includes('created') ||
        col.field.toLowerCase().includes('updated')
      )
      dateColumnField = dateColumn?.field
    }

    if (dateColumnField) {
      result = result.filter(row => {
        const rowDate = row[dateColumnField!]
        // Include rows with null/undefined dates when date range filter is active
        if (!rowDate) return true
        
        const date = dayjs(rowDate).toDate()
        return date >= dateRange.value![0]! && date <= dateRange.value![1]!
      })
    }
  }

  // Apply custom select filters
  Object.keys(filterValues.value).forEach(key => {
    const filterValue = filterValues.value[key]
    if (filterValue !== null && filterValue !== undefined && filterValue !== '') {
      result = result.filter(row => {
        const rowValue = row[key]
        // Handle both direct value matching and nested object matching
        if (typeof rowValue === 'object' && rowValue !== null) {
          return JSON.stringify(rowValue) === JSON.stringify(filterValue)
        }
        return rowValue === filterValue || String(rowValue).includes(String(filterValue))
      })
    }
  })

  // Apply text search filter
  if (searchText.value && props.searchColumns.length > 0) {
    const searchLower = searchText.value.toLowerCase()
    result = result.filter(row => {
      return props.searchColumns.some(column => {
        const value = row[column]
        if (value === null || value === undefined) return false
        return String(value).toLowerCase().includes(searchLower)
      })
    })
  } else if (searchText.value && props.searchColumns.length === 0 && !props.dataFilter && columnsToUse.value.length > 0) {
    // If no specific columns provided, search in all columns (only if not using dataFilter)
    const searchLower = searchText.value.toLowerCase()
    result = result.filter(row => {
      return columnsToUse.value.some(column => {
        const value = row[column.field]
        if (value === null || value === undefined) return false
        return String(value).toLowerCase().includes(searchLower)
      })
    })
  }

  return result
})

// Computed: Search placeholder
const searchPlaceholderComputed = computed(() => {
  if (props.dataFilter) {
    const searchFilter = filterObject.value.find((f: any) => f.type === 'search')
    if (searchFilter) {
      return searchFilter.placeholder || props.searchPlaceholder
    }
  }
  return props.searchPlaceholder
})

// Helper functions
const isDateColumn = (field: string): boolean => {
  return field.toLowerCase().includes('date') || 
         field.toLowerCase().includes('fecha') ||
         field.toLowerCase().includes('created') ||
         field.toLowerCase().includes('updated')
}

const formatDateValue = (date: string | null | undefined): string => {
  if (!date) return ''
  const d = dayjs(date)
  return d.isValid() ? d.format('DD/MM/YYYY') : ''
}

const shouldFormatDate = (field: string): boolean => {
  return isDateColumn(field)
}

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url)
    return true
  } catch (error) {
    return false
  }
}

const shouldRenderAsUrl = (field: string, row: any): boolean => {
  // Check if there's a corresponding _url field
  const urlField = `${field}_url` || field.replace('_name', '_url')
  return row[urlField] && isValidUrl(row[urlField])
}

const getUrlValue = (column: ColumnDefinition, row: any): string => {
  const urlField = column.urlField || `${column.field}_url`
  return row[urlField] || row[column.field] || ''
}

const getUrlLabel = (column: ColumnDefinition, row: any): string => {
  return row[column.field] || getUrlValue(column, row)
}

const shouldTruncate = (field: string): boolean => {
  // Auto-truncate common long text fields
  return field.toLowerCase().includes('comment') ||
         field.toLowerCase().includes('description') ||
         field.toLowerCase().includes('note') ||
         field.toLowerCase().includes('text')
}

const getUserName = (userId: number | null | undefined): string => {
  if (!userId) return ''
  const user = authStore.getUserById(userId)
  return user?.name || ''
}

const getTagLabel = (column: ColumnDefinition, value: any): string => {
  if (!column.tagConfig) return String(value || '')
  const option = column.tagConfig.options.find(opt => opt.value === value)
  return option?.label || String(value || '')
}

const getTagSeverity = (column: ColumnDefinition, value: any): 'info' | 'success' | 'warning' | 'danger' => {
  if (!column.tagConfig) return 'info'
  const option = column.tagConfig.options.find(opt => opt.value === value)
  return option?.severity || column.tagConfig.defaultSeverity || 'info'
}

const getColumnValue = (row: any, field: string): any => {
  // Handle nested fields like "client.name"
  if (field.includes('.')) {
    const parts = field.split('.')
    let value = row
    for (const part of parts) {
      value = value?.[part]
      if (value === null || value === undefined) return null
    }
    return value
  }
  return row[field]
}

const truncateText = (text: string | null | undefined, maxLength: number = 11): string => {
  if (!text) return ''
  const textStr = String(text)
  return textStr.length > maxLength ? textStr.substring(0, maxLength) + '...' : textStr
}

const getTooltipValue = (text: string | null | undefined): string | object => {
  if (!text) return ''
  // Return tooltip configuration for PrimeVue
  return {
    value: text,
    showDelay: 200,
    hideDelay: 0,
    tooltipStyleClass: 'max-w-md whitespace-normal break-words'
  }
}

// Methods
const onSearchChange = () => {
  // Update filterValues if using dataFilter search filter
  if (props.dataFilter) {
    const searchFilter = filterObject.value.find((f: any) => f.type === 'search')
    if (searchFilter) {
      filterValues.value[searchFilter.id] = searchText.value
    }
  }
  
  emit('filter-change', {
    search: searchText.value,
    filters: filterValues.value,
    dateRange: dateRange.value
  })
}

const onFilterChange = () => {
  // DEBUG: Log filter change
  console.log('🔄 [List.vue] Filter changed:', {
    filterValues: filterValues.value,
    searchText: searchText.value,
    dateRange: dateRange.value
  })
  
  emit('filter-change', {
    search: searchText.value,
    filters: filterValues.value,
    dateRange: dateRange.value
  })
}

const onDateRangeChange = async () => {
  emit('date-range-change', dateRange.value)
  emit('filter-change', {
    search: searchText.value,
    filters: filterValues.value,
    dateRange: dateRange.value
  })
  
  // If dataFilter is provided and dates are active, fetch data with date range
  if (props.dataFilter?.fetch_content && props.showDateRange) {
    // Only fetch if both dates are set
    if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
      internalLoading.value = true
      try {
        await props.dataFilter.fetch_content({
          dateRange: dateRange.value,
          filters: filterValues.value
        })
      } catch (error) {
        console.error('Error fetching data with date range:', error)
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar los datos',
          life: 5000
        })
      } finally {
        internalLoading.value = false
      }
    }
  }
}

const getRowUrl = (row: any): string | null => {
  if (props.dataFilter?.rowClickUrl) {
    return typeof props.dataFilter.rowClickUrl === 'function'
      ? props.dataFilter.rowClickUrl(row)
      : props.dataFilter.rowClickUrl.replace('{id}', row.id)
  }

  if (props.dataFilter?.url_edit) {
    return `${props.dataFilter.url_edit}${row.id}`
  }

  if (props.listConfig?.rowClickUrl) {
    return typeof props.listConfig.rowClickUrl === 'function'
      ? props.listConfig.rowClickUrl(row)
      : props.listConfig.rowClickUrl.replace('{id}', row.id)
  }

  return null
}

const openRowUrl = (url: string, openInNewTab: boolean) => {
  if (openInNewTab) {
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }
  window.location.href = url
}

const onRowClick = (event: any) => {
  const row = event.data
  const mouseEvent = event.originalEvent as MouseEvent | undefined
  const isMiddleClick = mouseEvent?.button === 1
  const isNewTabModifier = !!(mouseEvent?.ctrlKey || mouseEvent?.metaKey)
  emit('row-click', row)
  
  // Use dataFilter.load_row_props if available
  if (props.dataFilter?.load_row_props) {
    const rowProps = props.dataFilter.load_row_props(row)
    if (rowProps?.onClick && !isMiddleClick && !isNewTabModifier) {
      rowProps.onClick(event)
      return
    }
  }

  const url = getRowUrl(row)
  if (!url) return

  openRowUrl(url, isMiddleClick || isNewTabModifier)
}

const getPtRowData = (options: any): any => {
  return options?.context?.data
    ?? options?.context?.rowData
    ?? options?.props?.rowData
    ?? options?.props?.data
    ?? null
}

const onRowMouseDown = (e: MouseEvent, row: any) => {
  if (e.button !== 1) return
  // Prevent browser auto-scroll mode on middle click inside table rows.
  e.preventDefault()
  e.stopPropagation()
  const url = getRowUrl(row)
  if (!url) return
  openRowUrl(url, true)
}

const onRowAuxClick = (e: MouseEvent, row: any) => {
  if (e.button !== 1) return
  e.preventDefault()
  e.stopPropagation()
  const url = getRowUrl(row)
  if (!url) return
  openRowUrl(url, true)
}

const applyAdvancedFilters = () => {
  showFilterDialog.value = false
  emit('filter-change', {
    search: searchText.value,
    filters: filterValues.value,
    dateRange: dateRange.value
  })
}

const dataTablePt = {
  bodyrow: (options: any) => ({
    onMousedown: (e: MouseEvent) => onRowMouseDown(e, getPtRowData(options)),
    onAuxclick: (e: MouseEvent) => onRowAuxClick(e, getPtRowData(options))
  })
}

// Action handlers
const handleEdit = (row: any) => {
  emit('edit', row)
  
  // Use dataFilter.url_edit if available
  if (props.dataFilter?.url_edit) {
    window.location.href = `${props.dataFilter.url_edit}${row.id}`
    return
  }
  
  if (props.actionButtons?.edit && typeof props.actionButtons.edit === 'string') {
    const url = props.actionButtons.edit.replace('{id}', row.id)
    window.location.href = url
  } else if (props.listConfig?.editUrl) {
    const url = typeof props.listConfig.editUrl === 'function'
      ? props.listConfig.editUrl(row)
      : props.listConfig.editUrl.replace('{id}', row.id)
    window.location.href = url
  }
}

const handleDelete = (row: any) => {
  selectedRow.value = row
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!selectedRow.value) return
  
  deleting.value = true
  try {
    // Use dataFilter.deleteEvent if available
    if (props.dataFilter?.deleteEvent) {
      await props.dataFilter.deleteEvent(selectedRow.value.id)
      emit('delete', selectedRow.value)
      
      // Refresh data
      if (props.dataFilter.fetch_content) {
        await props.dataFilter.fetch_content()
      }
      
      toast.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Registro eliminado correctamente',
        life: 3000
      })
    } else if (props.listConfig?.deleteFunction) {
      await props.listConfig.deleteFunction(selectedRow.value.id)
      emit('delete', selectedRow.value)
      
      // Refresh data if refresh function is provided
      if (props.listConfig.refreshFunction) {
        await props.listConfig.refreshFunction()
      }
      
      toast.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Registro eliminado correctamente',
        life: 3000
      })
    } else {
      emit('delete', selectedRow.value)
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudo eliminar el registro',
      life: 5000
    })
  } finally {
    deleting.value = false
    showDeleteDialog.value = false
    selectedRow.value = null
  }
}

const handleDuplicate = async (row: any) => {
  duplicatingId.value = row.id
  try {
    // Use dataFilter.url_duplicate if available
    if (props.dataFilter?.url_duplicate) {
      const { axiosToken } = await import('@/utils/http')
      await axiosToken('post', `${props.dataFilter.url_duplicate}${row.id}`)
      emit('duplicate', row)
      
      // Refresh data
      if (props.dataFilter.fetch_content) {
        await props.dataFilter.fetch_content()
      }
      
      toast.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Registro duplicado correctamente',
        life: 3000
      })
    } else if (props.listConfig?.duplicateFunction) {
      await props.listConfig.duplicateFunction(row.id)
      emit('duplicate', row)
      
      // Refresh data if refresh function is provided
      if (props.listConfig.refreshFunction) {
        await props.listConfig.refreshFunction()
      }
      
      toast.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Registro duplicado correctamente',
        life: 3000
      })
    } else {
      emit('duplicate', row)
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudo duplicar el registro',
      life: 5000
    })
  } finally {
    duplicatingId.value = null
  }
}

const handleDownload = async (row: any) => {
  emit('download', row)
  
  try {
    // Use dataFilter.downloadEvent if available
    if (props.dataFilter?.downloadEvent) {
      await props.dataFilter.downloadEvent(row.id)
      toast.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Descarga iniciada',
        life: 3000
      })
    } else if (props.listConfig?.downloadFunction) {
      await props.listConfig.downloadFunction(row.id)
      toast.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Descarga iniciada',
        life: 3000
      })
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudo descargar el archivo',
      life: 5000
    })
  }
}

const getDownloadFilenameFromHeaders = (headers: any): string | null => {
  const contentDisposition = headers?.['content-disposition'] || headers?.['Content-Disposition']
  if (!contentDisposition || typeof contentDisposition !== 'string') {
    return null
  }
  const match = contentDisposition.match(/filename="?([^"]+)"?/)
  return match?.[1] || null
}

const handleCsvDownload = async () => {
  if (!props.csvDownloadUrl) return
  downloadingCsv.value = true
  try {
    const queryParams: Record<string, string> = {}
    // if (props.showDateRange && dateRange.value?.[0] && dateRange.value?.[1]) {
    //   queryParams.date_from = dayjs(dateRange.value[0]).format('YYYY-MM-DD')
    //   queryParams.date_to = dayjs(dateRange.value[1]).format('YYYY-MM-DD')
    // }

    const response = await axiosToken('get', props.csvDownloadUrl, queryParams)
    const csvRaw = typeof response?.data === 'string' ? response.data : JSON.stringify(response?.data ?? '')
    const blob = new Blob([csvRaw], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = getDownloadFilenameFromHeaders(response?.headers) || props.csvDownloadFilename || 'export.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudo descargar el CSV',
      life: 5000
    })
  } finally {
    downloadingCsv.value = false
  }
}

// Watch for data changes to reset filters if needed
watch(() => props.data, () => {
  // You can add logic here to handle data changes
}, { deep: true })

// Cleanup resize listener and ResizeObserver
onUnmounted(() => {
  window.removeEventListener('resize', updateViewportHeight)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})
</script>

<style scoped>
:deep(.p-datatable .p-datatable-thead > tr > th) {
  background-color: #f9fafb;
  font-weight: 600;
}

:deep(.p-datatable .p-datatable-tbody > tr) {
  cursor: pointer;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background-color: #f3f4f6;
}
</style>
